import { generateIsomerDepiction } from '@peruselab/structure-editor/chemistry/isomerDepiction';
import {
  aromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { drawingDocumentToSvg } from '@peruselab/structure-editor/rendering/svgExport';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { ChemIsomerV1Document } from '@peruselab/structure-editor/domain/isomerDocument';

const THUMBNAIL_COORDINATE_SIZE = { width: 240, height: 200 };
const THUMBNAIL_COLOR = '#1f2933';

/**
 * #128/#130 -- an isomer drawn for display: a fresh 2D layout of its molecule with its own
 * stereochemistry (wedges and hashes), through the same SVG export the "Export SVG" button uses.
 * `idPrefix` namespaces the inlined SVG's <marker> ids (#429), so several on one page don't clash.
 * Shared by IsomerThumbnail and the reaction scheme (#745).
 */
export async function isomerSvg(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  idPrefix: string
): Promise<string> {
  const document = await generateIsomerDepiction(molecule, isomer, THUMBNAIL_COORDINATE_SIZE);
  return drawingDocumentToSvg(
    document,
    {
      bondOrders: drawnBondOrders(document),
      aromaticBondIds: aromaticBondIds(document),
      stereoBonds: bondStereoValues(document),
      overriddenBondIds: new Set()
    },
    THUMBNAIL_COLOR,
    // #984 -- the document's own coordinate size, as MoleculeThumbnail does (#518):
    // THUMBNAIL_COORDINATE_SIZE is only the depiction's floor, so a larger molecule's coordinates
    // re-normalized onto it were squeezed unevenly (x and y by different factors) and drawn
    // stretched -- every structure in a reaction card was.
    { width: document.depiction.coordinateSet.width, height: document.depiction.coordinateSet.height },
    idPrefix.replace(/[^A-Za-z0-9_-]/g, '')
  );
}
